import { ArrowLeft } from 'lucide-react';
import { type ComponentProps, type ReactNode, useEffect, useRef } from 'react';

import { cn } from '../lib/cn';
import { Button } from './button';

export interface MasterDetailProps extends Omit<ComponentProps<'div'>, 'children'> {
  /** 목록 열 — 보통 `SelectableList`. */
  list: ReactNode;
  /** 고른 항목의 상세. 없으면(`null`) 아무것도 고르지 않은 상태다. */
  detail: ReactNode;
  /** 아무것도 고르지 않았을 때 본문 — 보통 `EmptyState`. 넓은 화면에서만 보인다. */
  empty: ReactNode;
  /** 좁은 화면에서 상세를 덮은 채 목록으로 돌아간다 — 보통 선택(`?detail=`)을 지운다. */
  onBack: () => void;
  /** @default '목록으로' */
  backLabel?: string;
  /** 목록 영역의 접근 이름. */
  listLabel?: string;
  /**
   * 맥락 레일 — 본문 옆에서 돕는 것(근거·맥락·다음 단계). 1180 초과에서 오른쪽 열,
   * 1180 이하에서 본문 아래, 760 이하에서 본문 다음에 이어진다.
   */
  rail?: ReactNode;
  /** 레일 영역의 접근 이름. */
  railLabel?: string;
  /**
   * 760 미만에서 목록을 어떻게 둘지. `overlay`(기본): 상세가 목록을 덮고 뒤로 버튼이 붙는다(D147 ⑥).
   * `stack`: 목록이 본문 위에 쌓인다 — 본문이 늘 있는 화면에 쓴다.
   * `picker`: 목록을 숨긴다 — 화면이 머리 아래에 고르기 버튼을 따로 둔다(D2 §5.2 접힘).
   */
  narrow?: 'overlay' | 'stack' | 'picker';
  /** 본문 열의 폭. `read`(기본)는 읽기 폭(640)에서 멈춘다. */
  mainWidth?: 'read' | 'full';
}

/**
 * 작업면 — 지면 한 장을 1px 선으로 목록 · 본문 · (레일) 열로 나눈다 (D2 §5.2 · D150).
 *
 * 열: 목록 248(1180 이하 220) · 본문 유동(좌 패딩 40/32/16) · 레일 clamp(296, 26%, 360).
 * 760 이상에서 목록과 본문이 나란하고, 그 아래에서는 `narrow`대로 접힌다.
 * 열마다 따로 스크롤하므로 부모가 높이를 준다(`PageShell fill`). 작업면 안에 카드를 두지 않는다.
 * 선택 상태는 이 컴포넌트가 쥐지 않는다 — 앱에서는 `useDetailSelection`이 URL로 쥔다(E14 §3).
 * 좁은 폭에서는 초점도 옮긴다(E14 §14): 상세가 덮으면 뒤로 버튼으로, 뒤로 가면 마지막으로 고른 목록 항목으로.
 */
export function MasterDetail({
  list,
  detail,
  empty,
  onBack,
  backLabel = '목록으로',
  listLabel,
  rail,
  railLabel,
  narrow = 'overlay',
  mainWidth = 'read',
  className,
  ...props
}: MasterDetailProps) {
  const open = detail !== null && detail !== undefined && detail !== false;
  const overlay = narrow === 'overlay';
  const stacked = narrow === 'stack';
  const backRef = useRef<HTMLButtonElement>(null);
  const lastListFocus = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(open);
  const listRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return undefined;
    const remember = (event: FocusEvent) => {
      if (event.target instanceof HTMLElement) lastListFocus.current = event.target;
    };
    list.addEventListener('focusin', remember);
    return () => list.removeEventListener('focusin', remember);
  }, []);

  useEffect(() => {
    if (!overlay || wasOpen.current === open) return;
    wasOpen.current = open;
    if (isSplit()) return;
    if (open) backRef.current?.focus();
    else if (lastListFocus.current?.isConnected) lastListFocus.current.focus();
  }, [open, overlay]);

  return (
    <div
      className={cn(
        'bg-page border-line rounded-t-card grid min-h-0 flex-1 overflow-hidden border border-b-0',
        stacked ? 'grid-cols-1 grid-rows-[auto_minmax(0,1fr)]' : 'grid-cols-1',
        'split:grid-cols-[var(--lf-list-width)_minmax(0,1fr)] split:grid-rows-1',
        className,
      )}
      {...props}
    >
      <aside
        aria-label={listLabel}
        ref={listRef}
        className={cn(
          'border-line px-snug pt-row pb-panel min-h-0 flex-col overflow-y-auto',
          'split:flex split:border-r split:border-b-0',
          stacked && 'border-b',
          (overlay && open) || narrow === 'picker' ? 'hidden' : 'flex',
        )}
      >
        {list}
      </aside>
      <div
        className={cn(
          'split:flex min-h-0 flex-col overflow-y-auto',
          rail &&
            'rail:grid rail:grid-cols-[minmax(0,1fr)_var(--lf-rail-width)] rail:overflow-hidden',
          overlay && !open ? 'hidden' : 'flex',
        )}
      >
        <div
          className={cn('py-panel px-(--lf-main-pad)', rail && 'rail:min-h-0 rail:overflow-y-auto')}
        >
          <div className={cn('flex flex-col', mainWidth === 'read' && 'max-w-page-read')}>
            {open ? (
              <>
                {overlay ? (
                  <div className="split:hidden pb-card">
                    <Button
                      ref={backRef}
                      variant="ghost"
                      size="sm"
                      icon={ArrowLeft}
                      onClick={onBack}
                    >
                      {backLabel}
                    </Button>
                  </div>
                ) : null}
                {detail}
              </>
            ) : (
              empty
            )}
          </div>
        </div>
        {rail ? (
          <aside
            aria-label={railLabel}
            className={cn(
              'border-line py-panel border-t px-(--lf-main-pad)',
              'rail:px-pane rail:min-h-0 rail:overflow-y-auto rail:border-t-0 rail:border-l',
            )}
          >
            {rail}
          </aside>
        ) : null}
      </div>
    </div>
  );
}

/** 목록과 본문이 나란한 폭(760 이상)이면 목록이 가려지지 않으므로 초점을 옮기지 않는다. */
function isSplit() {
  return (
    typeof window !== 'undefined' && window.matchMedia?.('(min-width: 47.5rem)').matches === true
  );
}
